/*
 *  KAN_WebGPUSupport.js
 *  Project: Kanoogi Library System
 *
 *  Copyright © 2026 Kanoogi Inc. | All Rights Reserved
 *
 *  The game runs on WebGPU only (docs/webgpu-conversion-plan.md §0 and §9, option G3, decided
 *  2026-10-06). The front end does NOT need WebGPU, and it is the one that tells the player,
 *  before Start; the game says so too if a player reaches it anyway. This file is the ONE copy
 *  of what those surfaces share, so the lobby, the Rock War menu and the game cannot drift:
 *
 *    ProbeWebGPU()        can this browser start WebGPU? navigator.gpu plus requestAdapter,
 *                         bounded by a timeout. Never needs WebGPU itself, and never throws.
 *    RequestWebGPUDevice() an adapter AND a device for a surface that draws, under one
 *                         deadline; a device that answers late is destroyed. Never throws.
 *    RendererFailure(r)   has the game's renderer failed? Reads KAN_WebGPU2's initFailed /
 *                         deviceLost and KAN_Renderer's webglRequested.
 *    WebGPUWords(reason)  the player-worded title and message for a reason.
 *    BrowserSuggestions() which browsers run it on THIS device, with install links. Shown only
 *                         when the player asks ("Which browsers work?"), never up front.
 *    BrowserListNode()    that list as DOM; WebGPUNoticeNode() / ShowWebGPUDialog() the front
 *                         end's notice and dialog around it.
 *
 *  Everything but the DOM builders is pure, and those take the document as an argument, so
 *  tests/webgpu-required.test.mjs runs the lot under Node.
 *
 *  Reasons (one vocabulary for probe, renderer and beacons):
 *    no_webgpu      the browser has no navigator.gpu
 *    no_adapter     navigator.gpu is there but requestAdapter() found no usable GPU
 *    adapter_error  requestAdapter() threw
 *    device_failed  the adapter was found but the device would not start (requestDevice, setup)
 *    device_lost    the GPU device was lost after it started (driver reset, GPU removed)
 *    timeout        no answer in time: UNKNOWN to the probe (not a block), a failure to the game
 *    webgl_requested  the game was asked for its old WebGL engine (?renderer=webgl, or a stored
 *                   kanoogi_renderer=webgl), which it no longer has. The game only.
 */

"use strict";

/* ---------------------------------------------------------------- */

// Browser support, from web.dev "WebGPU is now supported in major browsers" (checked
// 2026-10-11): Chrome/Edge 113+ on Windows, macOS and ChromeOS; Chrome 121+ on Android 12+
// with Qualcomm or ARM GPUs; Firefox 141+ on Windows and 145+ on Apple-silicon macOS Tahoe 26;
// Safari 26 on macOS Tahoe 26, iOS 26 and iPadOS 26. Linux is experimental in every browser.
// Install links were each fetched (HTTP 200) on the same day.

const LINKS=Object.freeze(
	{
	chrome:'https://www.google.com/chrome/',
	chromeAndroid:'https://play.google.com/store/apps/details?id=com.android.chrome',
	edge:'https://www.microsoft.com/edge/download',
	firefox:'https://www.mozilla.org/firefox/new/',
	});

export const PROBE_TIMEOUT_MS=4000;

const TITLE='This game needs WebGPU';
const WORDS=Object.freeze(
	{
	no_webgpu:{title:TITLE,message:'Rock War draws its battles with WebGPU, and this browser does not support it, so the match cannot start here.'},
	no_adapter:{title:TITLE,message:'This browser supports WebGPU but could not find a graphics card it is allowed to use. That happens with some older graphics cards, in virtual machines and over remote desktop, and when hardware acceleration is turned off in the browser\'s settings.'},
	adapter_error:{title:TITLE,message:'WebGPU would not start in this browser, so the match cannot be drawn. Updating the browser and the graphics drivers usually fixes it.'},
	device_failed:{title:TITLE,message:'WebGPU would not start on this device, so the match cannot be drawn. Updating the browser and the graphics drivers usually fixes it.'},
	device_lost:{title:'Graphics stopped',message:'The graphics card stopped responding, so the game can no longer draw. Your match is still running on the server: reload to rejoin it.'},
	timeout:{title:'Graphics did not start',message:'The graphics system did not finish starting, so the match cannot be drawn. Reload to try again.'},
	webgl_requested:{title:TITLE,message:'This game was asked to use its old WebGL renderer, which it no longer has: Rock War draws its battles with WebGPU only. Go back to the lobby and start the match again.'},
	});

export const BROWSERS_LABEL='Which browsers work?';

// WebGPUWords(reason) -> {title, message}. An unknown reason gets the generic no-WebGPU words
// rather than nothing: the player must always be told something.
export function WebGPUWords(reason)
	{
	return(WORDS[reason]||WORDS.device_failed);
	}

// Is this a reason the browser itself is to blame for, so a different browser might help?
// device_lost and timeout are not: the same browser usually works after a reload. Nor is
// webgl_requested: the browser was never asked to start WebGPU.
export function SuggestsOtherBrowser(reason)
	{
	return(reason!=='device_lost' && reason!=='timeout' && reason!=='webgl_requested');
	}
/* ---------------------------------------------------------------- */

// ProbeWebGPU({nav, timeoutMs}) -> Promise<{ok, reason}>
//   ok:true   an adapter came back: WebGPU should start
//   ok:false  it cannot: no navigator.gpu, no adapter, or requestAdapter threw
//   ok:null   no answer within timeoutMs: UNKNOWN. Callers must not turn a player away on
//             this - a slow adapter is not a missing one, and the game still says so if it
//             really cannot start.
// Never throws, and asks only for an ADAPTER (no device), so the front end does not hold GPU
// resources. `nav` is injectable for tests.

export async function ProbeWebGPU({nav=(typeof navigator!=='undefined'?navigator:undefined), timeoutMs=PROBE_TIMEOUT_MS}={})
	{
	if (!nav || !nav.gpu || typeof nav.gpu.requestAdapter!=='function') return({ok:false,reason:'no_webgpu'});

	const TIMEOUT={};
	let timer=null;
	try
		{
		const adapter=await Promise.race(
			[
			nav.gpu.requestAdapter(),
			new Promise((resolve)=>{ timer=setTimeout(()=>resolve(TIMEOUT),timeoutMs); })
			]);
		if (adapter===TIMEOUT) return({ok:null,reason:'timeout'});
		if (!adapter) return({ok:false,reason:'no_adapter'});
		return({ok:true,reason:'ok'});
		}
	catch (err)
		{
		return({ok:false,reason:'adapter_error'});
		}
	finally
		{
		clearTimeout(timer);
		}
	}

// ProbeOnce() - one probe per page, shared by every caller on it.
let probeOnce=null;
export function ProbeOnce(opts)
	{
	if (!probeOnce) probeOnce=ProbeWebGPU(opts);
	return(probeOnce);
	}

// Blocks(result) - true only for a definite no. Unknown (timeout) is never a block.
export function Blocks(result)
	{
	return(!!result && result.ok===false);
	}

// RequestWebGPUDevice({nav, timeoutMs, adapterOptions, deviceDescriptor})
//   -> Promise<{ok:true, reason:'ok', adapter, device, format}
//            | {ok:false, reason, detail}>
// For a surface that DRAWS with WebGPU and has somewhere else to go when it cannot (the site
// logos fall back to WebGL, then to their <img>). Same reasons as ProbeWebGPU, plus
// device_failed; here a timeout is ok:false, because a caller cannot draw with a device it
// does not have. Never throws.
//
// ONE deadline covers the adapter AND the device, so the worst case is timeoutMs, not twice
// it: a caller is holding something hidden while it waits (the logo's placeholder is
// CSS-hidden until it either draws or falls back). A request that loses the race is not
// cancelled - WebGPU has no way to - so a device that answers after the deadline is
// destroyed: nobody holds it, and leaked devices are how requestDevice() comes to hang at all
// (Kanlibs/techtree/stage.js).

export async function RequestWebGPUDevice({nav=(typeof navigator!=='undefined'?navigator:undefined), timeoutMs=PROBE_TIMEOUT_MS, adapterOptions, deviceDescriptor}={})
	{
	const gpu=nav && nav.gpu;
	if (!gpu || typeof gpu.requestAdapter!=='function') return({ok:false,reason:'no_webgpu',detail:'no navigator.gpu'});

	const TIMEOUT={};
	let expired=false, timer=null;
	const deadline=new Promise((resolve)=>{ timer=setTimeout(()=>{ expired=true; resolve(TIMEOUT); },timeoutMs); });
	const race=(p)=>Promise.race([p,deadline]);
	const why=(err)=>String((err && err.message) || err || 'unknown');
	try
		{
		let adapter;
		try { adapter=await race(Promise.resolve().then(()=>gpu.requestAdapter(adapterOptions))); }
		catch (err) { return({ok:false,reason:'adapter_error',detail:why(err)}); }
		if (adapter===TIMEOUT) return({ok:false,reason:'timeout',detail:`no adapter within ${timeoutMs} ms`});
		if (!adapter) return({ok:false,reason:'no_adapter',detail:'requestAdapter() returned null'});

		const asked=Promise.resolve().then(()=>adapter.requestDevice(deviceDescriptor));
		asked.then((late)=>{ if (expired && late) { try { late.destroy(); } catch (err) { /* already gone */ } } },()=>{});
		let device;
		try { device=await race(asked); }
		catch (err) { return({ok:false,reason:'device_failed',detail:why(err)}); }
		if (device===TIMEOUT) return({ok:false,reason:'timeout',detail:`no device within ${timeoutMs} ms`});
		if (!device) return({ok:false,reason:'device_failed',detail:'requestDevice() returned nothing'});

		const format=(typeof gpu.getPreferredCanvasFormat==='function')?gpu.getPreferredCanvasFormat():'bgra8unorm';
		return({ok:true,reason:'ok',adapter,device,format});
		}
	finally
		{
		clearTimeout(timer);
		}
	}
/* ---------------------------------------------------------------- */

// RendererFailure(renderer) -> null | {reason, detail}
// Has the game's renderer failed for good? Three sources:
//   - KAN_WebGPU2 set deviceLost (and the loss was not a deliberate destroy()).
//   - KAN_WebGPU2 set initFailed, with initFailReason when it knows why. With no navigator.gpu
//     that is no_webgpu, set during construction, so it wins over the next one.
//   - KAN_Renderer set webglRequested: the game was asked for the WebGL engine it no longer
//     has (the plan's step 5 deleted it), from the source it names.
// A renderer that is merely not ready yet is NOT a failure; that is the caller's timeout.

export function RendererFailure(r)
	{
	if (!r) return(null);
	if (r.deviceLost && r.lostReason!=='destroyed') return({reason:'device_lost',detail:String(r.lostMessage||'')});
	if (r.initFailed) return({reason:WORDS[r.initFailReason]?r.initFailReason:'device_failed',detail:String(r.initFailDetail||'')});
	if (r.webglRequested) return({reason:'webgl_requested',detail:`renderer=webgl from ${r.webglRequested}; the game runs on WebGPU only`});
	return(null);
	}
/* ---------------------------------------------------------------- */

// DetectPlatform({ua, maxTouchPoints}) -> 'ios' | 'android' | 'chromeos' | 'windows' | 'mac' | 'linux' | 'other'
// iPadOS 13+ asks for the desktop site and reports a Mac user agent; a Mac with a touch
// screen does not exist, so touch points tell them apart.

export function DetectPlatform({ua='', maxTouchPoints=0}={})
	{
	const s=String(ua);
	if (/iPhone|iPad|iPod/i.test(s)) return('ios');
	if (/Macintosh/i.test(s) && maxTouchPoints>1) return('ios');
	if (/Android/i.test(s)) return('android');
	if (/CrOS/i.test(s)) return('chromeos');
	if (/Windows/i.test(s)) return('windows');
	if (/Macintosh|Mac OS X/i.test(s)) return('mac');
	if (/Linux|X11/i.test(s)) return('linux');
	return('other');
	}

// BrowserSuggestions({ua, maxTouchPoints}) -> {platform, lead, browsers:[{name, href, note}], foot}
// The browsers that run the match on the player's own kind of device, best first. href is
// null where there is nothing to download (Safari comes with the system).

export function BrowserSuggestions(env={})
	{
	const platform=DetectPlatform(env);
	const chrome={name:'Google Chrome',href:LINKS.chrome,note:'Free download'};
	const edge={name:'Microsoft Edge',href:LINKS.edge,note:'Free download'};
	const safariMac={name:'Safari 26',href:null,note:'Comes with macOS Tahoe 26: System Settings → General → Software Update'};
	const out={platform,lead:'',browsers:[],foot:'Already using one of these? Update it to the latest version, and check that hardware acceleration is turned on in its settings.'};

	switch (platform)
		{
		case 'ios':
			out.browsers=[{name:'Safari',href:null,note:'Update to iOS 26 or iPadOS 26: Settings → General → Software Update'}];
			break;
		case 'android':
			out.browsers=[{name:'Google Chrome',href:LINKS.chromeAndroid,note:'Android 12 or newer, on most phones and tablets'}];
			out.foot='Some phones\' graphics chips are not supported yet. Already using Chrome? Update it from the Play Store.';
			break;
		case 'chromeos':
			out.browsers=[{name:'Google Chrome',href:null,note:'Built in: keep ChromeOS up to date in Settings → About ChromeOS'}];
			break;
		case 'windows':
			out.browsers=[chrome,edge,{name:'Mozilla Firefox',href:LINKS.firefox,note:'Version 141 or newer'}];
			out.foot='Already using one of these? Update it, and update your graphics drivers: WebGPU on Windows needs a DirectX 12 graphics card.';
			break;
		case 'mac':
			out.browsers=[chrome,safariMac,edge,{name:'Mozilla Firefox',href:LINKS.firefox,note:'Version 145 or newer, on Apple-silicon Macs with macOS Tahoe 26'}];
			break;
		case 'linux':
			out.lead='WebGPU on Linux is still experimental in every major browser, so the match may not start here. It runs on Windows, macOS, ChromeOS, Android 12 or newer, and iPhone or iPad with iOS 26.';
			out.browsers=[{name:'Google Chrome',href:LINKS.chrome,note:'Linux support is experimental'}];
			break;
		default:
			out.browsers=[chrome,edge,{name:'Mozilla Firefox',href:LINKS.firefox,note:'Version 141 or newer on Windows'},{name:'Safari',href:null,note:'Version 26, on macOS Tahoe 26, iOS 26 or iPadOS 26'}];
			break;
		}
	return(out);
	}

// The calling page's own device, for BrowserSuggestions().
export function ThisDevice()
	{
	if (typeof navigator==='undefined') return({});
	return({ua:navigator.userAgent||'',maxTouchPoints:navigator.maxTouchPoints||0});
	}
/* ---------------------------------------------------------------- */

// DOM. textContent only, never innerHTML. The CSS is injected once per document and themes
// itself from whatever it sits in: --kmodal-* (the lobby's dialogs; --kmodal-hover is this
// file's own), then --kan-scene-*, then the game's orange-on-black. Links and buttons are at least 44 px tall (touch rule).

const CSS=`
.kan-gpu-browsers { margin-top:16px; text-align:left; }
.kan-gpu-lead, .kan-gpu-foot { margin:0 0 10px; font-size:14px; line-height:1.5; color:inherit; opacity:0.85; }
.kan-gpu-foot { margin:10px 0 0; }
.kan-gpu-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
.kan-gpu-item
	{
	display:flex; flex-direction:column; justify-content:center; gap:2px;
	min-height:44px; box-sizing:border-box; padding:8px 12px;
	border:1px solid var(--kmodal-accent, var(--kan-scene-border, rgba(255,134,5,0.6)));
	border-radius:8px; color:inherit; text-decoration:none;
	}
a.kan-gpu-item:hover, a.kan-gpu-item:focus-visible { background:var(--kmodal-hover, rgba(255,134,5,0.12)); }
a.kan-gpu-item:focus-visible { outline:2px solid var(--kmodal-accent-bright, #FFB146); outline-offset:1px; }
.kan-gpu-name { font-weight:600; color:var(--kmodal-accent-bright, var(--kan-scene-accent, #FFB146)); }
a.kan-gpu-item .kan-gpu-name::after { content:' ↗'; font-weight:400; }
.kan-gpu-note { font-size:13px; line-height:1.4; opacity:0.8; overflow-wrap:anywhere; }
.kan-gpu-notice { text-align:left; }
.kan-gpu-notice-title { margin:0 0 8px; font-size:20px; color:var(--kmodal-accent-bright, var(--kan-scene-accent, #FFB146)); }
.kan-gpu-notice-msg { margin:0; font-size:15px; line-height:1.5; }
.kan-gpu-btn
	{
	min-height:44px; padding:8px 18px; margin-top:14px; border-radius:8px; cursor:pointer;
	background:transparent; color:var(--kmodal-accent-bright, var(--kan-scene-accent, #FFB146));
	border:2px solid var(--kmodal-accent, var(--kan-scene-border, rgb(255,134,5)));
	font:inherit; font-size:15px;
	}
.kan-gpu-btn:hover, .kan-gpu-btn:focus-visible { background:var(--kmodal-hover, rgba(255,134,5,0.12)); }
.kan-gpu-backdrop
	{
	position:fixed; inset:0; display:flex; align-items:center; justify-content:center;
	padding:16px; box-sizing:border-box; background:rgba(0,0,0,0.8); z-index:1100;
	}
.kan-gpu-panel
	{
	position:relative; width:min(460px,100%); max-height:100%; overflow-y:auto; box-sizing:border-box;
	padding:24px 22px 22px; border-radius:12px;
	background:var(--kmodal-bg, var(--kan-scene-bg, #0c0c0c));
	border:2px solid var(--kmodal-accent, var(--kan-scene-border, rgb(255,134,5)));
	color:var(--kmodal-text, var(--kan-scene-text, #fdf4e8));
	font-family:var(--kmodal-font, var(--kan-scene-font, sans-serif));
	}
.kan-gpu-panel .kan-gpu-notice-title { padding-right:40px; }
.kan-gpu-close
	{
	position:absolute; top:6px; right:6px; width:44px; height:44px; border:0; background:transparent;
	color:inherit; font-size:26px; line-height:1; cursor:pointer;
	}
.kan-gpu-actions { display:flex; flex-wrap:wrap; gap:10px; justify-content:flex-end; }
`;

export function InjectWebGPUCss(doc)
	{
	if (!doc || doc.getElementById('kan-gpu-css')) return;
	const style=doc.createElement('style');
	style.id='kan-gpu-css';
	style.textContent=CSS;
	(doc.head||doc.documentElement).appendChild(style);
	}

// BrowserListNode(doc, suggestions) - the suggestions as a block of links.
export function BrowserListNode(doc, s=BrowserSuggestions(ThisDevice()))
	{
	InjectWebGPUCss(doc);
	const wrap=doc.createElement('div');
	wrap.className='kan-gpu-browsers';
	if (s.lead)
		{
		const p=doc.createElement('p');
		p.className='kan-gpu-lead';
		p.textContent=s.lead;
		wrap.appendChild(p);
		}
	const ul=doc.createElement('ul');
	ul.className='kan-gpu-list';
	for (const b of s.browsers)
		{
		const li=doc.createElement('li');
		const item=doc.createElement(b.href?'a':'div');
		item.className='kan-gpu-item';
		if (b.href)
			{
			item.href=b.href;
			item.target='_blank';
			item.rel='noopener noreferrer';
			}
		const name=doc.createElement('span');
		name.className='kan-gpu-name';
		name.textContent=b.name;
		const note=doc.createElement('span');
		note.className='kan-gpu-note';
		note.textContent=b.note;
		item.append(name,note);
		li.appendChild(item);
		ul.appendChild(li);
		}
	wrap.appendChild(ul);
	if (s.foot)
		{
		const p=doc.createElement('p');
		p.className='kan-gpu-foot';
		p.textContent=s.foot;
		wrap.appendChild(p);
		}
	return(wrap);
	}

// WebGPUNoticeNode(doc, {reason, suggestions, onBrowsers}) - title, message and, when another
// browser could help, a "Which browsers work?" button that reveals the list in place. The
// list is built on the first press, not before: it is there when the player asks for it.
export function WebGPUNoticeNode(doc, {reason='no_webgpu', suggestions, onBrowsers}={})
	{
	InjectWebGPUCss(doc);
	const words=WebGPUWords(reason);
	const box=doc.createElement('div');
	box.className='kan-gpu-notice';
	const h=doc.createElement('h2');
	h.className='kan-gpu-notice-title';
	h.textContent=words.title;
	const p=doc.createElement('p');
	p.className='kan-gpu-notice-msg';
	p.textContent=words.message;
	box.append(h,p);
	if (SuggestsOtherBrowser(reason))
		{
		const btn=doc.createElement('button');
		btn.type='button';
		btn.className='kan-gpu-btn';
		btn.textContent=BROWSERS_LABEL;
		btn.setAttribute('aria-expanded','false');
		let list=null;
		btn.addEventListener('click',()=>
			{
			if (!list)
				{
				list=BrowserListNode(doc,suggestions||BrowserSuggestions(ThisDevice()));
				box.appendChild(list);
				if (typeof onBrowsers==='function') onBrowsers();
				}
			else list.hidden=!list.hidden;
			btn.setAttribute('aria-expanded',String(!list.hidden));
			});
		box.appendChild(btn);
		}
	return(box);
	}

// ShowWebGPUDialog({reason, doc, closeLabel, onBrowsers}) - the front end's dialog: the notice
// in a themed panel with a close button. Escape, the × and a press on the backdrop close it.
// Returns {close, element}.
export function ShowWebGPUDialog({reason='no_webgpu', doc=(typeof document!=='undefined'?document:null), closeLabel='OK', onBrowsers}={})
	{
	if (!doc) return({close:()=>{},element:null});
	const prev=doc.getElementById('kan-gpu-dialog');
	if (prev) prev.remove();

	const backdrop=doc.createElement('div');
	backdrop.id='kan-gpu-dialog';
	backdrop.className='kan-gpu-backdrop';
	backdrop.setAttribute('role','dialog');
	backdrop.setAttribute('aria-modal','true');
	const panel=doc.createElement('div');
	panel.className='kan-gpu-panel';
	backdrop.appendChild(panel);

	const OnKey=(e)=>{ if (e.key==='Escape') { e.preventDefault(); e.stopPropagation(); Close(); } };	// the dialog's Escape: the page's own (the Rock War menu's Back) must not ALSO fire
	const Close=()=>{ doc.removeEventListener('keydown',OnKey,true); backdrop.remove(); };

	const x=doc.createElement('button');
	x.type='button';
	x.className='kan-gpu-close';
	x.setAttribute('aria-label','Close');
	x.textContent='×';
	x.addEventListener('click',Close);

	const notice=WebGPUNoticeNode(doc,{reason,onBrowsers});
	const h=notice.querySelector('.kan-gpu-notice-title');
	if (h) { h.id='kan-gpu-dialog-title'; backdrop.setAttribute('aria-labelledby',h.id); }

	const actions=doc.createElement('div');
	actions.className='kan-gpu-actions';
	const ok=doc.createElement('button');
	ok.type='button';
	ok.className='kan-gpu-btn';
	ok.textContent=closeLabel;
	ok.addEventListener('click',Close);
	actions.appendChild(ok);

	panel.append(x,notice,actions);
	backdrop.addEventListener('pointerdown',(e)=>{ if (e.target===backdrop) Close(); });
	doc.addEventListener('keydown',OnKey,true);
	doc.body.appendChild(backdrop);
	const first=notice.querySelector('.kan-gpu-btn')||ok;
	try { first.focus(); } catch (err) { /* focus is a nicety */ }
	return({close:Close,element:backdrop});
	}
